import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Form } from "react-bootstrap";
import DualRangeSlider from "./DualRangeSlider";

interface DistanceFilterProps {
  onFilterChange?: (minDistance: number, maxDistance: number) => void;
}

export interface DistanceFilterRef {
  reset: () => void;
}

const DistanceFilter = forwardRef<DistanceFilterRef, DistanceFilterProps>(({ onFilterChange }, ref) => {
  const MIN_DISTANCE = 0;
  const MAX_DISTANCE = 200;
  const STEP = 5;
  
  const [minDistance, setMinDistance] = useState<number>(MIN_DISTANCE);
  const [maxDistance, setMaxDistance] = useState<number>(MAX_DISTANCE);
  const isInitialMount = useRef(true);

  // Expose reset function to parent component
  useImperativeHandle(ref, () => ({
    reset: () => {
      setMinDistance(MIN_DISTANCE);
      setMaxDistance(MAX_DISTANCE);
      if (onFilterChange) {
        onFilterChange(MIN_DISTANCE, MAX_DISTANCE);
      }
    }
  }));

  useEffect(() => {
    // Skip the effect on initial mount to prevent infinite loops
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Notify parent component when filter values change
    if (onFilterChange) {
      onFilterChange(minDistance, maxDistance);
    }
  }, [minDistance, maxDistance, onFilterChange]);

  const handleRangeChange = (min: number, max: number) => {
    setMinDistance(min);
    setMaxDistance(max);
  };

  return (
    <div className="distance-filter">
      <Form.Group>
        <DualRangeSlider
          min={MIN_DISTANCE}
          max={MAX_DISTANCE}
          step={STEP}
          unit="km"
          initialMin={minDistance}
          initialMax={maxDistance}
          onChange={handleRangeChange}
        />
      </Form.Group>
    </div>
  );
});

export default DistanceFilter;
